import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SkeletonHero } from '../components/ui/page-skeleton';
import { ComingSoonPanel } from '../components/ComingSoonPanel';
import SurveillancePanel from '../components/SurveillancePanel';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { DataTable } from '../components/DataTable';
import {
  ArrowLeft, Storefront, Wrench, TestTube, ChartLineUp,
  Plus, Drop, Lightning, FloppyDisk, Spinner,
  CheckCircle, XCircle, Trash, Receipt, Link as LinkIcon,
  PencilSimple, Cpu, Tag, GraduationCap, Buildings,
  CurrencyDollar, Phone, IdentificationCard, Clock, User as UserIcon,
  ChartPieSlice, VideoCamera, Money,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import RoyaltyPanel from '../components/finance/RoyaltyPanel';

const STORE_CATEGORIES = [
  { value: '1 Door', label: '1 Door' },
  { value: '2 Doors', label: '2 Doors' },
  { value: '3 Doors', label: '3 Doors' },
  { value: '4 Doors', label: '4 Doors' },
];
const INST_TYPES = [
  { value: 'University', label: 'University' },
  { value: 'Hospital', label: 'Hospital' },
  { value: 'School', label: 'School' },
  { value: 'Office', label: 'Office' },
  { value: 'Public', label: 'Public' },
];
const INPUT_WATER_TYPES = [
  { value: 'Fresh Water', label: 'Fresh Water' },
  { value: 'Tap Water', label: 'Tap Water' },
  { value: 'Borehole', label: 'Borehole' },
  { value: 'Rain Water', label: 'Rain Water' },
  { value: 'Salty Water', label: 'Salty Water' },
];
const OPERATIONAL_STATUS_OPTIONS = [
  { value: 'Operating', label: 'Operating' },
  { value: 'Fault', label: 'Fault' },
  { value: 'Under Maintenance', label: 'Under Maintenance' },
];
const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

const MAINTENANCE_TYPES = [
  { value: 'filter_change', label: 'Filter Change' },
  { value: 'uv_bulb',       label: 'UV Bulb' },
  { value: 'pump',          label: 'Pump' },
  { value: 'sanitisation',  label: 'Sanitisation' },
  { value: 'general',       label: 'General' },
];

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function StoreDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { entities } = useTenant();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [store, setStore] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [activeTab, setActiveTab] = useState('analytics');
  const [form, setForm] = useState({});
  const [editOpen, setEditOpen] = useState(false);

  const fetchCampuses = useCallback(async (entityId) => {
    if (!entityId) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/campuses?entity_id=${entityId}`,
        { headers: authHeaders(), withCredentials: true });
      setCampuses(Array.isArray(data) ? data : []);
    } catch (e) { setCampuses([]); }
  }, []);

  const fetchStore = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/stores/${id}`,
        { headers: authHeaders(), withCredentials: true });
      setStore(data);
      setForm({
        store_no: data.store_no || '',
        store_name: data.store_name || '',
        device_id: data.device_id || '',
        entity_id: data.entity_id || '',
        campus_id: data.campus_id || '',
        category: data.category || '',
        inst_type: data.inst_type || '',
        input_water: data.input_water || '',
        mobile: data.mobile || '',
        permit_no: data.permit_no || '',
        selling_price: String(data.selling_price ?? '0'),
        utility_water: String(data.utility_water ?? '0'),
        utility_electricity: String(data.utility_electricity ?? '0'),
        royalty: String(data.royalty ?? '0'),
        operational_status: data.operational_status || 'Operating',
        status: (data.status === 'active') ? 'Active' : (data.status === 'inactive' ? 'Inactive' : data.status || 'Active'),
      });
      if (data.entity_id) fetchCampuses(data.entity_id);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load store'));
      navigate('/stores');
    } finally {
      setLoading(false);
    }
  }, [id, fetchCampuses, navigate]);

  useEffect(() => { fetchStore(); }, [fetchStore]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        selling_price: parseFloat(form.selling_price) || 0,
        utility_water: parseFloat(form.utility_water) || 0,
        utility_electricity: parseFloat(form.utility_electricity) || 0,
        royalty: parseFloat(form.royalty) || 0,
      };
      await axios.put(`${API_URL}/api/stores/${id}`, payload,
        { headers: authHeaders(), withCredentials: true });
      toast.success('Store updated');
      setEditOpen(false);
      await fetchStore();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to update store'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !store) {
    return <div className="py-2"><SkeletonHero testid="store-detail-loading" /></div>;
  }

  const isOperating = (store.operational_status || '').toLowerCase() === 'operating';
  const opTone =
    (store.operational_status || '').toLowerCase() === 'operating'         ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' :
    (store.operational_status || '').toLowerCase() === 'fault'             ? 'bg-rose-50 text-rose-700 ring-rose-200' :
    (store.operational_status || '').toLowerCase().includes('maintenance') ? 'bg-amber-50 text-amber-700 ring-amber-200' :
                                                                              'bg-slate-50 text-slate-700 ring-slate-200';
  const statusTone =
    String(store.status || '').toLowerCase() === 'active'
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : 'bg-slate-50 text-slate-600 ring-slate-200';

  return (
    <div className="space-y-4 pb-6" data-testid="store-detail-page">
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/stores')} className="h-8 w-8 p-0 mt-1" data-testid="back-btn">
          <ArrowLeft size={16} />
        </Button>
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <span className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isOperating ? 'bg-emerald-500/15' : 'bg-rose-500/15'}`}>
            <Storefront size={22} className={isOperating ? 'text-emerald-700' : 'text-rose-700'} weight="duotone" />
          </span>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl font-bold text-[#1C1917] tracking-tight truncate" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {store.store_name}
              </h1>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ${opTone}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {store.operational_status || '—'}
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wider ring-1 ring-inset ${statusTone}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {store.status || '—'}
              </span>
            </div>
            <p className="text-xs text-[#78716C] truncate font-mono mt-1">
              {[
                store.store_code || store.store_no,
                store.device_id ? `Device ${store.device_id}` : null,
                store.campus_name,
                // Hide the entity name for entity-scoped users — they only
                // ever see their own entity, so the breadcrumb is noise.
                isSuperAdmin ? store.entity_name : null,
              ].filter(Boolean).join(' · ') || '—'}
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
        <TabsList className="bg-[#FCFAF6] rounded-xl p-1 h-auto">
          <TabsTrigger value="analytics"   className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-analytics">
            <ChartLineUp size={14} weight="duotone" /> Overview
          </TabsTrigger>
          <TabsTrigger value="details"     className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-details">
            <Storefront size={14} weight="duotone" /> Store Details
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-maintenance">
            <Wrench size={14} weight="duotone" /> Maintenance
          </TabsTrigger>
          <TabsTrigger value="qc"          className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-qc">
            <TestTube size={14} weight="duotone" /> Quality Control
          </TabsTrigger>
          <TabsTrigger value="surveillance" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-surveillance">
            <VideoCamera size={14} weight="duotone" /> Surveillance
          </TabsTrigger>
          <TabsTrigger value="royalty" className="rounded-lg px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5 data-[state=active]:bg-[#0A2540] data-[state=active]:text-white" data-testid="tab-royalty">
            <Money size={14} weight="duotone" /> Royalty
          </TabsTrigger>
        </TabsList>

        <div className="rounded-2xl border border-[#EDE6D8] bg-[#FCFAF6]/30 p-3 sm:p-4" data-testid="store-tab-frame">
          {/* ── ANALYTICS (Overview, default landing) ── */}
          <TabsContent value="analytics" className="mt-0">
            <AnalyticsTab
              storeId={id}
              currency={
                ((entities || []).find(e => String(e.id) === String(store.entity_id))?.currency_code)
                || store.entity_currency
                || ''
              }
            />
          </TabsContent>

          {/* ── DETAILS (read-only view) ── */}
          <TabsContent value="details" className="mt-0">
            <StoreOverview
              store={store}
              campusName={(campuses.find(c => String(c.id) === String(store.campus_id))?.campus_name) || store.campus_name || '—'}
              entityName={((entities || []).find(e => String(e.id) === String(store.entity_id))?.entity_name) || store.entity_name || '—'}
              currency={
                ((entities || []).find(e => String(e.id) === String(store.entity_id))?.currency_code)
                || store.entity_currency
                || ''
              }
              isSuperAdmin={isSuperAdmin}
              onEdit={() => setEditOpen(true)}
            />
          </TabsContent>

          {/* ── MAINTENANCE — coming soon ── */}
          <TabsContent value="maintenance" className="mt-0">
            <ComingSoonPanel
              icon={Wrench}
              title="Maintenance"
              blurb="Service logs, scheduled maintenance and next-due tracking for this store are being refined and will return shortly."
            />
          </TabsContent>

          {/* ── QC — coming soon ── */}
          <TabsContent value="qc" className="mt-0">
            <ComingSoonPanel
              icon={TestTube}
              title="Quality Control"
              blurb="Water-quality test records, pass/fail tracking and historical trends are on the way."
            />
          </TabsContent>

          {/* ── SURVEILLANCE — live cameras ── */}
          <TabsContent value="surveillance" className="mt-0">
            <SurveillancePanel storeId={id} />
          </TabsContent>

          {/* ── ROYALTY — live ledger from middleware ── */}
          <TabsContent value="royalty" className="mt-0">
            <RoyaltyPanel scope="store" storeId={id} testIdPrefix="store-royalty" />
          </TabsContent>
        </div>
      </Tabs>

      {/* ── EDIT STORE DIALOG ── */}
      <Dialog open={editOpen} onOpenChange={(o) => setEditOpen(o)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Edit Store</DialogTitle>
            <DialogDescription>Update store properties. Changes sync to ecobridge.tech.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-6">
            <Section title="Basic Information">
              <div className="grid grid-cols-2 gap-4">
                <FField label="Store Number"   value={form.store_no}   onChange={v => setForm(p => ({ ...p, store_no: v }))}   testid="store-no" />
                <FField label="Store Name *"   value={form.store_name} onChange={v => setForm(p => ({ ...p, store_name: v }))} testid="store-name" />
                <FField label="Device ID *"    value={form.device_id}  onChange={v => setForm(p => ({ ...p, device_id: v }))}  testid="store-device-id"
                  icon={<Cpu size={12} className="text-[#0A2540]" />} />
              </div>
            </Section>
            <Section title="Assignment">
              <div className="grid grid-cols-2 gap-4">
                {isSuperAdmin && (
                  <div className="space-y-2">
                    <Label>Entity *</Label>
                    <Select value={form.entity_id} onValueChange={v => { setForm(p => ({ ...p, entity_id: v, campus_id: '' })); fetchCampuses(v); }}>
                      <SelectTrigger data-testid="store-entity"><SelectValue placeholder="Select entity" /></SelectTrigger>
                      <SelectContent>{entities.map(e => <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Campus *</Label>
                  <Select value={form.campus_id} onValueChange={v => setForm(p => ({ ...p, campus_id: v }))}>
                    <SelectTrigger data-testid="store-campus"><SelectValue placeholder="Select campus" /></SelectTrigger>
                    <SelectContent>{campuses.map(c => <SelectItem key={c.id} value={c.id}>{c.campus_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </Section>
            <Section title="Classification">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2"><Label>Category *</Label>
                  <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
                    <SelectTrigger data-testid="store-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{STORE_CATEGORIES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Institution *</Label>
                  <Select value={form.inst_type} onValueChange={v => setForm(p => ({ ...p, inst_type: v }))}>
                    <SelectTrigger data-testid="store-inst"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{INST_TYPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Input Water *</Label>
                  <Select value={form.input_water} onValueChange={v => setForm(p => ({ ...p, input_water: v }))}>
                    <SelectTrigger data-testid="store-water"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{INPUT_WATER_TYPES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </Section>
            <Section title="Pricing & Utilities">
              <div className="grid grid-cols-2 gap-4">
                <FField label="Selling Price" type="number" value={form.selling_price}       onChange={v => setForm(p => ({ ...p, selling_price: v }))} testid="store-price" />
                <FField label="Royalty (%)"   type="number" value={form.royalty}             onChange={v => setForm(p => ({ ...p, royalty: v }))}        testid="store-royalty" />
                <FField label="Utility Water"       type="number" value={form.utility_water}       onChange={v => setForm(p => ({ ...p, utility_water: v }))}       testid="store-util-water"
                  icon={<Drop size={12} className="text-[#0A2540]" />} />
                <FField label="Utility Electricity" type="number" value={form.utility_electricity} onChange={v => setForm(p => ({ ...p, utility_electricity: v }))} testid="store-util-elec"
                  icon={<Lightning size={12} className="text-[#0A2540]" />} />
              </div>
            </Section>
            <Section title="Additional">
              <div className="grid grid-cols-2 gap-4">
                <FField label="Mobile Number" value={form.mobile}    onChange={v => setForm(p => ({ ...p, mobile: v }))}    testid="store-mobile" />
                <FField label="Permit Number" value={form.permit_no} onChange={v => setForm(p => ({ ...p, permit_no: v }))} testid="store-permit" />
                <div className="space-y-2"><Label>Operational Status</Label>
                  <Select value={form.operational_status} onValueChange={v => setForm(p => ({ ...p, operational_status: v }))}>
                    <SelectTrigger data-testid="store-op-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{OPERATIONAL_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Status</Label>
                  <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                    <SelectTrigger data-testid="store-status"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </Section>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="save-store-btn">
                {saving ? <Spinner size={14} className="animate-spin mr-2" /> : <FloppyDisk size={14} className="mr-2" />}
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── READ-ONLY STORE OVERVIEW ──────────────────────────────────────────────
// Renders every field returned by the middleware /get_stores endpoint as
// labeled cards. Edit lives behind a separate "Edit" button (opens dialog).
function StoreOverview({ store, campusName, entityName, currency, isSuperAdmin, onEdit }) {
  const fmtMoney = (v) => {
    const n = Number(v ?? 0);
    if (Number.isNaN(n)) return '—';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtDate = (s) => {
    if (!s) return '—';
    const m = String(s).match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
    return m ? `${m[1]} · ${m[2]}` : String(s);
  };

  return (
    <div className="space-y-4">
      {/* Edit-only action card — store identity is already in the page header */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-4 flex items-center justify-end">
        <Button onClick={onEdit} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg" data-testid="store-edit-btn">
          <PencilSimple size={14} className="mr-2" />
          Edit Store
        </Button>
      </div>

      {/* Assignment */}
      <SectionCard title="Assignment" icon={Buildings}>
        <div className={`grid grid-cols-1 sm:grid-cols-2 ${isSuperAdmin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
          {isSuperAdmin && <Cell icon={Buildings} label="Entity" value={entityName} />}
          <Cell icon={GraduationCap}    label="Campus"      value={campusName} />
          <Cell icon={Tag}              label="Category"    value={store.category} mono />
          <Cell icon={Buildings}        label="Institution" value={store.inst_type} />
        </div>
      </SectionCard>

      {/* Classification */}
      <SectionCard title="Classification" icon={Drop}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Cell icon={Drop}             label="Input Water"        value={store.input_water} />
          <Cell icon={Cpu}              label="Device ID"          value={store.device_id} mono />
          <Cell icon={IdentificationCard} label="Permit Number"    value={store.permit_no} mono />
          <Cell icon={Phone}            label="Mobile"             value={store.mobile} mono />
        </div>
      </SectionCard>

      {/* Pricing & Utilities */}
      <SectionCard title="Pricing & Utilities" icon={CurrencyDollar}>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MoneyCell icon={CurrencyDollar} label="Selling Price"       value={fmtMoney(store.selling_price)}       unit={currency} />
          <MoneyCell icon={CurrencyDollar} label="Royalty"             value={`${fmtMoney(store.royalty)}%`}       unit="" />
          <MoneyCell icon={Drop}           label="Utility · Water"     value={fmtMoney(store.utility_water)}       unit={currency} />
          <MoneyCell icon={Lightning}      label="Utility · Electric"  value={fmtMoney(store.utility_electricity)} unit={currency} />
        </div>
      </SectionCard>

      {/* Audit */}
      <SectionCard title="Audit Trail" icon={Clock}>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Cell icon={UserIcon} label="Maker ID"       value={store.maker_id} mono />
          <Cell icon={Clock}    label="Date Registered" value={fmtDate(store.date_registered)} mono />
          <Cell icon={Clock}    label="Date Updated"    value={fmtDate(store.date_updated)}    mono />
        </div>
      </SectionCard>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
          <Icon size={14} weight="fill" />
        </span>
        <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {title}
        </p>
      </div>
      {children}
    </div>
  );
}

function Cell({ icon: Icon, label, value, mono }) {
  const v = (value == null || value === '') ? '—' : value;
  return (
    <div className="rounded-xl border border-[#EDE6D8] bg-[#FCFAF6]/40 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} weight="duotone" className="text-[#78716C]" />}
        <p className="text-[10px] uppercase tracking-[0.14em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {label}
        </p>
      </div>
      <p
        className={`text-sm font-semibold text-[#1C1917] truncate ${mono ? 'font-mono' : ''}`}
        style={mono ? undefined : { fontFamily: 'Plus Jakarta Sans, sans-serif' }}
        title={typeof v === 'string' ? v : undefined}
      >
        {v}
      </p>
    </div>
  );
}

function MoneyCell({ icon: Icon, label, value, unit }) {
  return (
    <div className="rounded-xl border border-[#EDE6D8] bg-[#FCFAF6]/40 p-3">
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={11} weight="duotone" className="text-[#78716C]" />}
        <p className="text-[10px] uppercase tracking-[0.14em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {label}
        </p>
      </div>
      <p className="text-base font-bold text-[#0A2540] font-mono">
        {value}
        {unit && <span className="ml-1 text-[10px] font-semibold text-[#78716C]">{unit}</span>}
      </p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold mb-3" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {title}
      </p>
      {children}
    </div>
  );
}

function FField({ label, value, onChange, type = 'text', testid, icon }) {
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1">{icon}{label}</Label>
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} className="border-[#EDE6D8] rounded-lg" data-testid={testid} />
    </div>
  );
}

function KpiCard({ label, value, accent = 'text-[#1C1917]', sub }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
      <p className={`mt-2 text-2xl font-bold font-mono ${accent}`}>{value}</p>
      {sub && <p className="text-[11px] text-[#78716C] mt-0.5 font-mono">{sub}</p>}
    </div>
  );
}

/* ── ANALYTICS ── */
import { SkBar } from '../components/ui/page-skeleton';
import {
  AreaChart, Area, BarChart as ReBarChart, Bar as ReBar,
  PieChart as RePieChart, Pie, Cell as ReCell,
  XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, LineChart, Line,
} from 'recharts';

function AnalyticsTab({ storeId, currency }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/stores/${storeId}/analytics`,
          { headers: authHeaders(), withCredentials: true });
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e, 'Failed to load analytics'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [storeId]);

  if (loading) return <StoreOverviewSkeleton />;
  if (!data)   return <p className="py-12 text-center text-sm text-[#78716C]">No analytics available.</p>;

  // Formatters
  const fmt  = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtI = (n) => Number(n || 0).toLocaleString();
  const fmtL = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 1 })} L`;
  const compact = (n) => {
    const v = Math.abs(Number(n || 0));
    if (v >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
    if (v >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
    return String(Math.round(n || 0));
  };

  // Delta today vs yesterday
  const todayAmt = data.today.amount || 0;
  const yAmt = data.yesterday?.amount || 0;
  const delta = yAmt > 0 ? ((todayAmt - yAmt) / yAmt) * 100 : (todayAmt > 0 ? 100 : 0);
  const deltaUp = delta >= 0;

  // Delta this month vs last month
  const monthAmt = data.month?.amount || 0;
  const lmAmt = data.last_month?.amount || 0;
  const monthDelta = lmAmt > 0 ? ((monthAmt - lmAmt) / lmAmt) * 100 : (monthAmt > 0 ? 100 : 0);
  const monthDeltaUp = monthDelta >= 0;

  // 30-day series for area chart + 7-day sparkline data
  const series30 = (data.daily_30 || []).map((d) => ({ ...d }));
  const series7  = (data.daily_sales || []).map((d) => ({ ...d }));

  // Channel donut
  const chColors = ['#0A2540', '#C9A35B', '#059669', '#7C3AED', '#DC2626', '#0EA5E9'];
  const channelData = (data.channels || []).map((c, i) => ({ name: c.channel, value: c.amount, txns: c.txns, color: chColors[i % chColors.length] }));

  // Hourly heatmap rows (6×4 grid)
  const hourly = data.hourly || [];
  const hMax = Math.max(...hourly.map(h => h.txns || 0), 0) || 1;

  // Weekday bar data
  const weekday = data.weekday || [];

  // Top buyers
  const buyers = data.top_buyers || [];
  const buyersMax = Math.max(...buyers.map(b => b.amount || 0), 0) || 1;

  // Sparkline component (for the hero tiles)
  const Sparkline = ({ points, stroke }) => (
    <ResponsiveContainer width="100%" height={36}>
      <LineChart data={points} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <Line type="monotone" dataKey="amount" stroke={stroke} strokeWidth={2} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );

  const tooltipStyle = { fontFamily: 'Plus Jakarta Sans, sans-serif', border: '1px solid #EDE6D8', borderRadius: '10px', fontSize: 12, boxShadow: '0 4px 16px -8px rgba(28,25,23,0.12)' };

  return (
    <div className="space-y-4" data-testid="store-overview">
      {/* ── HERO TILES — Big stat tiles with sparklines ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        <HeroTile
          label="Today's Sales"
          value={fmt(todayAmt)}
          unit={currency}
          icon={CurrencyDollar}
          accent="#0A2540"
          delta={delta}
          deltaUp={deltaUp}
          deltaLabel="vs yesterday"
        >
          <Sparkline points={series7} stroke="#0A2540" />
        </HeroTile>
        <HeroTile
          label="This Month"
          value={fmt(data.month.amount)}
          unit={currency}
          icon={ChartLineUp}
          accent="#0A2540"
          delta={monthDelta}
          deltaUp={monthDeltaUp}
          deltaLabel="vs last month"
        >
          <Sparkline points={series30} stroke="#0A2540" />
        </HeroTile>
        <HeroTile
          label="Volume Today"
          value={fmtL(data.today.volume)}
          icon={Drop}
          accent="#0A2540"
          sub={`Month: ${fmtL(data.month.volume)}`}
        >
          <Sparkline points={series7.map(d => ({ amount: d.volume }))} stroke="#0A2540" />
        </HeroTile>
      </div>

      {/* ── ROW 2: Past 7 Days (marquee chart) ── */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]"><ChartLineUp size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Sales Trend · Past 7 Days</p>
          </div>
          <p className="text-[11px] font-mono text-[#78716C]">in {currency}</p>
        </div>
        {series7.every(d => !d.amount) ? (
          <div className="h-[220px] flex items-center justify-center"><p className="text-xs text-[#78716C]">No sales in the last 7 days.</p></div>
        ) : (
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <ReBarChart data={series7} margin={{ top: 16, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} tickFormatter={compact} />
                <ReTooltip
                  contentStyle={tooltipStyle}
                  labelStyle={{ color: '#0A2540', fontWeight: 600, marginBottom: 4 }}
                  formatter={(v, _n, ctx) => [`${fmt(v)} ${currency} · ${fmtI(ctx?.payload?.txns)} txn`, 'Sales']}
                />
                <ReBar dataKey="amount" radius={[8, 8, 0, 0]} maxBarSize={64}>
                  {series7.map((_, i) => (<ReCell key={i} fill={i === series7.length - 1 ? '#0A2540' : '#C9A35B'} />))}
                </ReBar>
              </ReBarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ── ROW 3: Weekday bar (1/2) + Hour heatmap (1/2) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">        <ChartCard title="By Weekday · MTD" icon={ChartPieSlice}>
          {weekday.every(d => !d.amount) ? (
            <p className="h-44 flex items-center justify-center text-xs text-[#78716C]">No sales this month.</p>
          ) : (
            <div className="h-44">
              <ResponsiveContainer width="100%" height="100%">
                <ReBarChart data={weekday} margin={{ top: 6, right: 4, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                  <XAxis dataKey="day" tick={{ fontSize: 9, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 9, fontFamily: 'JetBrains Mono, monospace' }} stroke="#78716C" axisLine={false} tickLine={false} tickFormatter={compact} />
                  <ReTooltip contentStyle={tooltipStyle} formatter={(v) => [`${fmt(v)} ${currency}`, 'Sales']} />
                  <ReBar dataKey="amount" radius={[6, 6, 0, 0]} fill="#059669" />
                </ReBarChart>
              </ResponsiveContainer>
            </div>
          )}
        </ChartCard>

        <ChartCard title="Hour-of-Day · MTD" icon={Clock}>
          {/* 24-cell heatmap row */}
          <div className="grid grid-cols-12 gap-1 px-1">
            {hourly.map((h) => {
              const intensity = h.txns / hMax;
              const bg = h.txns === 0 ? '#F2EBDC' : `rgba(10,37,64,${0.2 + 0.8 * intensity})`;
              return (
                <div key={h.hour} className="aspect-square rounded-md flex flex-col items-center justify-center" style={{ backgroundColor: bg }} title={`${h.hour}:00 · ${h.txns} txn`}>
                  <span className={`text-[9px] font-mono font-semibold ${intensity > 0.5 ? 'text-white' : 'text-[#1C1917]'}`}>
                    {String(h.hour).padStart(2, '0')}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex items-center justify-between text-[10px] font-mono text-[#78716C]">
            <span>Peak: {data.averages?.peak_hour_label || '—'}</span>
            <span>Busiest day: {data.averages?.busiest_weekday_label || '—'}</span>
          </div>
        </ChartCard>
      </div>

      {/* ── ROW 4: Averages strip ── */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]"><ChartPieSlice size={14} weight="fill" /></span>
          <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Averages & Peaks</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatTile label="Avg Sales / Day"  value={fmt(data.averages?.avg_amount_per_day || 0)} unit={currency} accent="#0A2540" />
          <StatTile label="Avg Volume / Day" value={fmtL(data.averages?.avg_volume_per_day || 0)} sub="lifetime" accent="#0A2540" />
          <StatTile label="Peak Hour"        value={data.averages?.peak_hour_label || '—'} sub={data.averages?.peak_hour ? `${data.averages.peak_hour}:00 · MTD` : 'no data'} accent="#0A2540" />
          <StatTile label="Busiest Weekday"  value={data.averages?.busiest_weekday_label || '—'} sub="this month" accent="#0A2540" />
        </div>
      </div>

      {/* ── ROW 5: Top buyers (2/3) + Recent sales (1/3) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#059669]/10 text-[#059669]"><Storefront size={14} weight="fill" /></span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Top Buyers · This Month</p>
          </div>
          {buyers.length === 0 ? (
            <p className="text-xs text-[#78716C] py-8 text-center">No purchases this month yet.</p>
          ) : (
            <div className="space-y-2.5">
              {buyers.map((b, i) => {
                const pct = (b.amount / buyersMax) * 100;
                return (
                  <div key={b.card_no || i} className="grid grid-cols-[28px_1fr_auto] items-center gap-3">
                    <span className="text-[10px] font-mono text-[#78716C] font-semibold">#{i + 1}</span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1C1917] font-mono truncate">Card {b.card_no}</p>
                      <div className="mt-1 h-1.5 rounded-full bg-[#FCFAF6] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: '#0A2540' }} />
                      </div>
                      <p className="text-[10px] text-[#78716C] font-mono mt-1">{fmtI(b.txns)} txn · {fmtL(b.volume)}</p>
                    </div>
                    <span className="text-sm font-mono font-bold text-[#059669] shrink-0">{fmt(b.amount)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#EDE6D8] bg-[#FCFAF6]/40 flex items-center gap-2">
            <Receipt size={13} className="text-[#0A2540]" weight="fill" />
            <p className="text-xs font-semibold uppercase tracking-wider text-[#1C1917]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Recent Sales · {data.recent_transactions.length}
            </p>
          </div>
          {data.recent_transactions.length === 0 ? (
            <p className="px-5 py-8 text-center text-xs text-[#78716C]">No sales on record yet.</p>
          ) : (
            <div className="max-h-72 overflow-y-auto">
              {data.recent_transactions.slice(0, 10).map((t, i) => (
                <div key={t.id || i} className="px-4 py-2.5 flex items-center justify-between border-b border-[#EDE6D8] last:border-b-0 hover:bg-[#FCFAF6]/40">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-[#1C1917] truncate">{t.narration || '—'}</p>
                    <p className="text-[10px] text-[#78716C] font-mono truncate">{t.trn_date} · {t.card_no || '—'}</p>
                  </div>
                  <p className="text-xs font-bold font-mono text-[#0A2540] shrink-0 ml-2">{compact(t.amount)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StoreOverviewSkeleton() {
  return (
    <div className="space-y-4" data-testid="store-overview-loading">
      {/* Hero row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                <SkBar className="h-3 w-1/3 mb-2" />
                <SkBar className="h-6 w-2/3 mb-2" />
                <SkBar className="h-3 w-1/2" />
              </div>
              <SkBar className="h-9 w-9 rounded-xl shrink-0" />
            </div>
            <SkBar className="mt-3 h-9 w-full rounded-md" />
          </div>
        ))}
      </div>

      {/* Marquee chart */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <SkBar className="h-4 w-48" />
          <SkBar className="h-3 w-12" />
        </div>
        <div className="h-[220px] flex items-end justify-between gap-3 px-1">
          {[0.4, 0.7, 0.55, 0.85, 0.45, 0.6, 0.95].map((h, i) => (
            <SkBar key={i} className="flex-1 rounded-t-md" style={{ height: `${h * 100}%` }} />
          ))}
        </div>
      </div>

      {/* Weekday + Hour heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <SkBar className="h-3 w-32 mb-4" />
          <div className="h-44 flex items-end justify-between gap-3">
            {[0.6, 0.4, 0.75, 0.55, 0.9, 0.5, 0.3].map((h, i) => (
              <SkBar key={i} className="flex-1 rounded-t-md" style={{ height: `${h * 100}%` }} />
            ))}
          </div>
        </div>
        <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <SkBar className="h-3 w-32 mb-4" />
          <div className="grid grid-cols-12 gap-1 px-1">
            {Array.from({ length: 24 }).map((_, i) => (
              <SkBar key={i} className="aspect-square rounded-md" />
            ))}
          </div>
        </div>
      </div>

      {/* Averages strip */}
      <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
        <SkBar className="h-3 w-40 mb-4" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-[#EDE6D8] bg-[#FCFAF6]/40 p-3">
              <SkBar className="h-2.5 w-2/3 mb-2" />
              <SkBar className="h-5 w-3/4 mb-1" />
              <SkBar className="h-2.5 w-1/2" />
            </div>
          ))}
        </div>
      </div>

      {/* Buyers + Recent */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white border border-[#EDE6D8] rounded-2xl p-5">
          <SkBar className="h-3 w-40 mb-4" />
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="grid grid-cols-[28px_1fr_auto] items-center gap-3 mb-3 last:mb-0">
              <SkBar className="h-3 w-5" />
              <div>
                <SkBar className="h-3 w-1/3 mb-2" />
                <SkBar className="h-1.5 w-full mb-1" />
                <SkBar className="h-2 w-1/4" />
              </div>
              <SkBar className="h-4 w-12" />
            </div>
          ))}
        </div>
        <div className="bg-white border border-[#EDE6D8] rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#EDE6D8] bg-[#FCFAF6]/40">
            <SkBar className="h-3 w-40" />
          </div>
          <div className="p-4 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <SkBar className="h-3 w-2/3 mb-1.5" />
                  <SkBar className="h-2.5 w-1/2" />
                </div>
                <SkBar className="h-3 w-10 ml-3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}


function HeroTile({ label, value, unit, icon: Icon, accent = '#0A2540', delta, deltaUp, deltaLabel, sub, children }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4 relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
          <p className="mt-1.5 text-2xl font-bold font-mono leading-tight truncate" style={{ color: accent }}>
            {value}
            {unit && <span className="ml-1 text-[10px] font-semibold text-[#78716C]">{unit}</span>}
          </p>
          {delta != null && (
            <div className="mt-1 flex items-center gap-1">
              <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold font-mono ${deltaUp ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                {deltaUp ? '▲' : '▼'} {Math.abs(delta).toFixed(0)}%
              </span>
              {deltaLabel && <span className="text-[10px] text-[#78716C] font-mono">{deltaLabel}</span>}
            </div>
          )}
          {sub && <p className="text-[10px] text-[#78716C] mt-1 font-mono truncate">{sub}</p>}
        </div>
        <span className="w-9 h-9 rounded-xl shrink-0 flex items-center justify-center" style={{ backgroundColor: `${accent}1A`, color: accent }}>
          <Icon size={16} weight="fill" />
        </span>
      </div>
      {children && <div className="mt-2">{children}</div>}
    </div>
  );
}

function ChartCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]"><Icon size={14} weight="fill" /></span>
        <p className="text-xs uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{title}</p>
      </div>
      {children}
    </div>
  );
}

function StatTile({ label, value, unit, sub, accent = '#0A2540' }) {
  return (
    <div className="rounded-xl border border-[#EDE6D8] bg-[#FCFAF6]/40 p-3">
      <p className="text-[10px] uppercase tracking-[0.14em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
      <p className="mt-1 text-lg font-bold font-mono leading-tight" style={{ color: accent }}>
        {value}
        {unit && <span className="ml-1 text-[10px] font-semibold text-[#78716C]">{unit}</span>}
      </p>
      {sub && <p className="text-[10px] text-[#78716C] mt-0.5 font-mono truncate">{sub}</p>}
    </div>
  );
}

function StatTileDup({ label, value, unit, sub, accent = '#0A2540' }) {
  // Unused duplicate kept only to avoid breaking external import references.
  // Real StatTile lives above in the dashboard block.
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.18em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
      <p className="mt-1.5 text-xl font-bold font-mono leading-tight" style={{ color: accent }}>
        {value}
        {unit && <span className="ml-1 text-[10px] font-semibold text-[#78716C]">{unit}</span>}
      </p>
      {sub && <p className="text-[11px] text-[#78716C] mt-0.5 font-mono">{sub}</p>}
    </div>
  );
}

/* ── MAINTENANCE ── */
function MaintenanceTab({ storeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    maintenance_type: 'general',
    technician: '', cost: '', notes: '', next_due: '',
  });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/stores/${storeId}/maintenance`,
        { headers: authHeaders(), withCredentials: true });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load maintenance log'));
    } finally { setLoading(false); }
  }, [storeId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const reset = () => setForm({
    date: new Date().toISOString().slice(0, 10),
    maintenance_type: 'general',
    technician: '', cost: '', notes: '', next_due: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/stores/${storeId}/maintenance`, {
        ...form, cost: parseFloat(form.cost) || 0,
      }, { headers: authHeaders(), withCredentials: true });
      toast.success('Maintenance entry recorded');
      setOpen(false); reset();
      fetchRows();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save'));
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Delete this maintenance entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/stores/${storeId}/maintenance/${entry.id}`,
        { headers: authHeaders(), withCredentials: true });
      toast.success('Entry deleted');
      fetchRows();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete'));
    }
  };

  const upcoming = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return rows
      .filter(r => r.next_due && r.next_due >= today)
      .sort((a, b) => a.next_due.localeCompare(b.next_due))[0] || null;
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#78716C]">Log servicing actions and track the next upcoming due date.</p>
        <Button onClick={() => setOpen(true)} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="add-maintenance-btn">
          <Plus size={14} className="mr-2" /> Log Maintenance
        </Button>
      </div>

      {upcoming && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-amber-800 font-semibold font-mono">Next Maintenance Due</p>
            <p className="text-sm font-semibold text-amber-900 mt-0.5">
              {MAINTENANCE_TYPES.find(t => t.value === upcoming.maintenance_type)?.label || upcoming.maintenance_type} · {upcoming.next_due}
            </p>
          </div>
          <Wrench size={20} className="text-amber-700" />
        </div>
      )}

      <DataTable
        columns={[
          { key: 'date', label: 'Date', width: 'minmax(110px,0.9fr)', mono: true, tone: 'strong' },
          { key: 'maintenance_type', label: 'Type', width: 'minmax(140px,1fr)', render: (r) => {
            const t = MAINTENANCE_TYPES.find(x => x.value === r.maintenance_type);
            return (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-full ring-1 ring-inset bg-[#0A2540]/10 text-[#0A2540] ring-[#0A2540]/20">
                <Wrench size={12} /> {t?.label || r.maintenance_type}
              </span>
            );
          } },
          { key: 'technician', label: 'Technician', width: 'minmax(140px,1.2fr)', tone: 'muted', render: (r) => r.technician || '—' },
          { key: 'cost', label: 'Cost', width: 'minmax(110px,0.9fr)', mono: true,
            render: (r) => Number(r.cost || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
          { key: 'next_due', label: 'Next Due', width: 'minmax(110px,0.9fr)', mono: true, tone: 'muted', render: (r) => r.next_due || '—' },
          { key: 'notes', label: 'Notes', width: 'minmax(180px,1.5fr)', tone: 'muted', render: (r) => r.notes || '—' },
        ]}
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        emptyIcon={<Wrench size={20} className="text-slate-500" />}
        emptyTitle="No maintenance entries yet"
        emptyText='Click "Log Maintenance" to record the first servicing.'
        testIdPrefix="maintenance"
        actionRender={(r) => (
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(r); }}
            className="text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10 h-8 w-8 p-0" aria-label="Delete">
            <Trash size={14} />
          </Button>
        )}
      />

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Maintenance</DialogTitle>
            <DialogDescription>Record a servicing action performed on this store.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <FField label="Date *"   type="date" value={form.date}       onChange={v => setForm(p => ({ ...p, date: v }))}       testid="m-date" />
              <FField label="Next Due" type="date" value={form.next_due}   onChange={v => setForm(p => ({ ...p, next_due: v }))}   testid="m-next" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Type *</Label>
                <Select value={form.maintenance_type} onValueChange={v => setForm(p => ({ ...p, maintenance_type: v }))}>
                  <SelectTrigger data-testid="m-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{MAINTENANCE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <FField label="Cost" type="number" value={form.cost} onChange={v => setForm(p => ({ ...p, cost: v }))} testid="m-cost" />
            </div>
            <FField label="Technician" value={form.technician} onChange={v => setForm(p => ({ ...p, technician: v }))} testid="m-tech" />
            <FField label="Notes"      value={form.notes}      onChange={v => setForm(p => ({ ...p, notes: v }))}      testid="m-notes" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button type="submit" className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="m-save">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── QUALITY CONTROL ── */
function QualityControlTab({ storeId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    ph: '', tds: '', chlorine: '', turbidity: '',
    passed: 'true', technician: '', attachment_url: '', notes: '',
  });

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/stores/${storeId}/quality-control`,
        { headers: authHeaders(), withCredentials: true });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load QC log'));
    } finally { setLoading(false); }
  }, [storeId]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const reset = () => setForm({
    date: new Date().toISOString().slice(0, 10),
    ph: '', tds: '', chlorine: '', turbidity: '',
    passed: 'true', technician: '', attachment_url: '', notes: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        date: form.date,
        ph: form.ph === '' ? null : parseFloat(form.ph),
        tds: form.tds === '' ? null : parseFloat(form.tds),
        chlorine: form.chlorine === '' ? null : parseFloat(form.chlorine),
        turbidity: form.turbidity === '' ? null : parseFloat(form.turbidity),
        passed: form.passed === 'true',
        technician: form.technician,
        attachment_url: form.attachment_url,
        notes: form.notes,
      };
      await axios.post(`${API_URL}/api/stores/${storeId}/quality-control`, payload,
        { headers: authHeaders(), withCredentials: true });
      toast.success('QC test recorded');
      setOpen(false); reset();
      fetchRows();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to save'));
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Delete this QC entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/stores/${storeId}/quality-control/${entry.id}`,
        { headers: authHeaders(), withCredentials: true });
      toast.success('Entry deleted');
      fetchRows();
    } catch (err) {
      toast.error(formatApiError(err, 'Failed to delete'));
    }
  };

  const passRate = useMemo(() => {
    if (rows.length === 0) return 0;
    return Math.round((rows.filter(r => r.passed).length / rows.length) * 100);
  }, [rows]);
  const lastTest = rows[0];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#78716C]">Log water-quality tests and monitor pass rate over time.</p>
        <Button onClick={() => setOpen(true)} className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="add-qc-btn">
          <Plus size={14} className="mr-2" /> Log Test
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <KpiCard label="Pass Rate"   value={`${passRate}%`} accent={passRate >= 80 ? 'text-emerald-700' : passRate >= 50 ? 'text-amber-700' : 'text-rose-700'} />
        <KpiCard label="Total Tests" value={String(rows.length)} accent="text-[#0A2540]" />
        <KpiCard
          label="Last Test"
          value={lastTest ? lastTest.date : '—'}
          accent={lastTest?.passed === false ? 'text-rose-700' : 'text-emerald-700'}
          sub={lastTest ? (lastTest.passed ? 'Passed' : 'Failed') : ''}
        />
      </div>

      <DataTable
        columns={[
          { key: 'date', label: 'Date', width: 'minmax(110px,0.9fr)', mono: true, tone: 'strong' },
          { key: 'ph',         label: 'pH',        width: 'minmax(70px,0.5fr)', mono: true, tone: 'muted', render: (r) => r.ph ?? '—' },
          { key: 'tds',        label: 'TDS',       width: 'minmax(70px,0.5fr)', mono: true, tone: 'muted', render: (r) => r.tds ?? '—' },
          { key: 'chlorine',   label: 'Chlorine',  width: 'minmax(80px,0.6fr)', mono: true, tone: 'muted', render: (r) => r.chlorine ?? '—' },
          { key: 'turbidity',  label: 'Turbidity', width: 'minmax(80px,0.6fr)', mono: true, tone: 'muted', render: (r) => r.turbidity ?? '—' },
          { key: 'technician', label: 'Technician', width: 'minmax(140px,1fr)', tone: 'muted', render: (r) => r.technician || '—' },
          { key: 'attachment_url', label: 'Doc', width: 'minmax(70px,0.5fr)', render: (r) => r.attachment_url
            ? <a href={r.attachment_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center text-[#0A2540] hover:underline"><LinkIcon size={14} /></a>
            : '—' },
          { key: 'passed', label: 'Result', width: 'minmax(110px,0.9fr)', render: (r) => r.passed ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-full ring-1 ring-inset bg-emerald-100 text-emerald-800 ring-emerald-300"><CheckCircle size={12} weight="fill" /> Passed</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-semibold rounded-full ring-1 ring-inset bg-rose-100 text-rose-800 ring-rose-300"><XCircle size={12} weight="fill" /> Failed</span>
          ) },
        ]}
        rows={rows}
        loading={loading}
        rowKey={(r) => r.id}
        emptyIcon={<TestTube size={20} className="text-slate-500" />}
        emptyTitle="No QC tests yet"
        emptyText='Click "Log Test" to record the first water-quality test.'
        testIdPrefix="qc"
        actionRender={(r) => (
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(r); }}
            className="text-[#EF4444] hover:text-[#EF4444] hover:bg-[#EF4444]/10 h-8 w-8 p-0" aria-label="Delete">
            <Trash size={14} />
          </Button>
        )}
      />

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Log Quality Test</DialogTitle>
            <DialogDescription>Capture water parameters and overall pass/fail result.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-1">
            <div className="grid grid-cols-2 gap-3">
              <FField label="Date *" type="date" value={form.date} onChange={v => setForm(p => ({ ...p, date: v }))} testid="qc-date" />
              <div className="space-y-2"><Label>Result</Label>
                <Select value={form.passed} onValueChange={v => setForm(p => ({ ...p, passed: v }))}>
                  <SelectTrigger data-testid="qc-passed"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Passed</SelectItem>
                    <SelectItem value="false">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <FField label="pH"        type="number" value={form.ph}        onChange={v => setForm(p => ({ ...p, ph: v }))}        testid="qc-ph" />
              <FField label="TDS"       type="number" value={form.tds}       onChange={v => setForm(p => ({ ...p, tds: v }))}       testid="qc-tds" />
              <FField label="Chlorine"  type="number" value={form.chlorine}  onChange={v => setForm(p => ({ ...p, chlorine: v }))}  testid="qc-chlorine" />
              <FField label="Turbidity" type="number" value={form.turbidity} onChange={v => setForm(p => ({ ...p, turbidity: v }))} testid="qc-turbidity" />
            </div>
            <FField label="Technician" value={form.technician} onChange={v => setForm(p => ({ ...p, technician: v }))} testid="qc-tech" />
            <FField label="Attachment URL" value={form.attachment_url} onChange={v => setForm(p => ({ ...p, attachment_url: v }))} testid="qc-attach" />
            <FField label="Notes" value={form.notes} onChange={v => setForm(p => ({ ...p, notes: v }))} testid="qc-notes" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
              <Button type="submit" className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white" data-testid="qc-save">Save</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
